import { Box, Chip, Stack, Typography } from "@mui/material";
import { GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { has, some } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetActivityLog, useGetAssignedChannelOptions, useGetCustomerStatusOptions, useGetLeadDetails, useGetSourceChannelOptions, useGetStatusOptions, useGetUserInterestsData, useSearchLeads, useUpdateLeadDetails } from "../../../api/leadsManagement";
import { MODULE } from "../../../constants/constants";
import { getStatusStyle, iconMapping } from "../../../helpers/leadsManagement";
import { UserActions } from "../../../models/Authorisation";
import { useUpdateLeadsDetails } from "../../../stores/leadsManagementStore";
import { usePermissionStore } from "../../../stores/permissionStore";
import { useUserStore } from "../../../stores/userStore";
import { LeadData, LeadsSearchState, UpdateLeadDetailPayload } from "../../../types/leads.types";
import { formatDateToMMDDYYYY, } from "../../../utils/date";
import { country } from "../../customer-360/components/SearchBar";

const useAllLeads = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient()
  const {
    editedNotes,
    editedStatus,
    setEditNotes,
    setEditStatus
  } = useUpdateLeadsDetails();

  const { countries, permissions, } = usePermissionStore();
  const { leadsUserAction } = useUserStore();
  const formMethods = useForm<LeadsSearchState>({
    defaultValues: { searchCriteriaOption: "ALL", searchText: "", status: "All Statuses", qualification: "All", sourceChannel: "All Channels", assignedChannel: "All Channels", country: "ALL" },
  });

  const { setValue } = formMethods
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<LeadData | null>(null);
  const { data: statusOptions } = useGetStatusOptions({
    permission_Token: usePermissionStore.getState().permissionToken ?? ""
  });

  const { data: sourceChannelOptions } = useGetSourceChannelOptions({
    permission_Token: usePermissionStore.getState().permissionToken ?? ""
  });

  const { data: assignedChannelOptions } = useGetAssignedChannelOptions({
    permission_Token: usePermissionStore.getState().permissionToken ?? ""
  });

  const { data: customerStatusOptionsResponse } = useGetCustomerStatusOptions({
    permission_Token: usePermissionStore.getState().permissionToken ?? ""
  })

  const customerStatusOptions = customerStatusOptionsResponse?.data?.options || []

  const searchLeadsMutation = useSearchLeads();
  const { data: leadsData, isLoading: isLeadsLoading,} = searchLeadsMutation;

  // Call search leads with default values on first load
  useEffect(() => {
    searchLeadsMutation.mutate({
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
      query: {},
      filterStatuses: [],
      filterSourceChannels: [],
      filterAssignedChannels: [],
      filterQualifications: [],
      filteredCountries: [],
      pagination: {
        CurrentPage: 1,
        pageSize: 10
      }
    });
  }, []);

  const leadDetailsMutation = useGetLeadDetails();
  const { data: selectedLeadData, isLoading: isLoadingLeadData } = leadDetailsMutation;
  const selectedLeadDetail = selectedLeadData?.data
  const userInterestsDataMutation = useGetUserInterestsData();
  const { data: userInterestsDetail, isLoading: isLoadingUserInterestsData } = userInterestsDataMutation;
  const userInterestsData = userInterestsDetail?.data

  const updateLeadDetailsMutation = useUpdateLeadDetails();
  const { isSuccess: isSuccessfullyUpdate, isLoading: isDetailUpdating } = updateLeadDetailsMutation;


  const { mutate: activityLogMutate, data: activityData, isLoading: isActivityLogLoading } = useGetActivityLog()

  useEffect(() => {
    if (isSuccessfullyUpdate) {
      setEditStatus(null);
      setEditNotes("");
      onCloseModal();

      // Reset form to default values
      formMethods.reset({
        searchCriteriaOption: "ALL",
        searchText: "",
        status: "All Statuses",
        qualification: "All",
        sourceChannel: "All Channels",
        assignedChannel: "All Channels",
        country: "ALL"
      });

      // Reset pagination
      setCurrentPage(1);

      // Call search with default values
      searchLeadsMutation.mutate({
        permission_Token: usePermissionStore.getState().permissionToken ?? "",
        query: {},
        filterStatuses: [],
        filterSourceChannels: [],
        filterAssignedChannels: [],
        filterQualifications: [],
        filteredCountries: [],
        pagination: {
          CurrentPage: 1,
          pageSize: 10
        }
      });
    }
  }, [isSuccessfullyUpdate])

  useEffect(() => {
    if (selectedRowData?.id) {
      activityLogMutate({
        permission_Token: usePermissionStore.getState().permissionToken ?? "",
        id: selectedRowData.id,
        pagination: {
          CurrentPage: 1,
          pageSize: 20
        }
      });
    }

  }, [selectedRowData])

  const isMaskedDataLevel = permissions?.find((item) => item.module === MODULE.LEADS)?.dataLevel !== "Full"
  const isLeadStatusWonOrLost = selectedLeadData?.data?.status === "Lost" || selectedLeadData?.data?.status === "Won"
  const canEdit = (leadsUserAction && Object.values(leadsUserAction).some(actions => actions?.includes(UserActions.Edit)) && !isLeadStatusWonOrLost) || false;

  const filterLeadsData = leadsData?.data?.records;
  const totalNumberOfLeads = leadsData?.data?.total || 0;
  const paginationData = leadsData?.data?.pagination;

  const countryOptions = useMemo(() => {
    if (countries) {
      const allCountries = countries?.find(val => val === "ALL");
      if (allCountries) {
        return country;
      } else {
        return countries?.map(item => ({
          label: item,
          value: item,
        }));
      }
    }
  }, [countries]);


  const statusOptionsArray = useMemo(() => {
    const defaultOption = { label: "All", value: "All Statuses" };
    const mappedOptions = (statusOptions?.data?.options || []).map((option: string) => ({
      label: option,
      value: option
    }));
    return [defaultOption, ...mappedOptions];
  }, [statusOptions]);

  const sourceChannelOptionsArray = useMemo(() => {
    const defaultOption = { label: "All Channels", value: "All Channels" };
    const mappedOptions = (sourceChannelOptions?.data?.options || []).map((option: string) => ({
      label: option,
      value: option
    }));
    return [defaultOption, ...mappedOptions];
  }, [sourceChannelOptions]);

  const assignedChannelOptionsArray = useMemo(() => {
    const defaultOption = { label: "All Channels", value: "All Channels" };
    const mappedOptions = (assignedChannelOptions?.data?.options || []).map((option: string) => ({
      label: option,
      value: option
    }));
    return [defaultOption, ...mappedOptions];
  }, [assignedChannelOptions]);

  const criteriaOptions = useMemo(() => {
    return [
      {
        label: "All Criteria",
        value: "ALL"
      },
      {
        label: "Name",
        value: "name"
      },
      {
        label: "Email Address",
        value: "emailAddress"
      },
      {
        label: "Contact Phone Number",
        value: "contactPhoneNumber"
      },
    ]
  }, [])

  const filterQualificationOptions = useMemo(() => {
    return [
      {
        label: "All",
        value: "All"
      },
      {
        label: "Hot",
        value: "Hot"
      },
      {
        label: "Warm",
        value: "Warm"
      },
      {
        label: "Cold",
        value: "Cold"
      },
    ]
  }, [])


  const columns: GridColDef<LeadData>[] = [
    {
      field: "qualification",
      headerName: t("platform.qualification"),
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => {
        const icon = iconMapping(params.row.qualification.toLocaleLowerCase(), { width: 20, height: 20 })

        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Typography>{icon} </Typography>
            <Typography>{params.row.qualification}</Typography>
          </Box>
        )

      }
    },
    // {
    //   field: "leadScore",
    //   headerName: "Lead Score",
    //   width: 110,
    //   renderCell: (params) => {
    //     return (
    //       <Chip
    //         label={params.value}
    //         sx={{
    //           backgroundColor: getScoreColor(params.value),
    //           color: "white",
    //           borderRadius: "50%",
    //           width: "30px",
    //           height: "30px",
    //           minWidth: "30px",
    //           "& .MuiChip-label": {
    //             padding: 0,
    //             fontSize: "14px",
    //             fontWeight: "bold"
    //           }
    //         }}
    //       />
    //     )

    //   }
    // },
    {
      field: "channel",
      headerName: t("platform.channel"),
      width: 110,
      sortable: false,
      filterable: false,
      type: "string",
      sortComparator: (v1, v2) => v1 < v2 ? -1 : v1 > v2 ? 1 : 0,
    },
    {
      field: "leadType",
      headerName: t("platform.leadType"),
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              backgroundColor: "white",
              borderColor: "#d0d0d0",
              color: "#333",
              fontWeight: 500,
              fontSize: "0.875rem"
            }}
          />
        )
      }
    },
    {
      field: "createdOn",
      headerName: t("platform.createdOn"),
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => {
        return (
          <Typography>{formatDateToMMDDYYYY(params.value)}</Typography>
        )
      }
    },
    {
      field: "profile",
      headerName: t("platform.profile"),
      width: 300,
      sortable: false,
      filterable: false,
      valueGetter: (params) => {
        return params.row.fullName || "";
      },
      sortComparator: (v1, v2) => {
        const name1 = (v1 || "").toString().toLowerCase();
        const name2 = (v2 || "").toString().toLowerCase();
        return name1.localeCompare(name2);
      },
      renderCell: (params) => {
        return <Stack
          direction={"column"}
          sx={{ alignItems: "flex-start", justifyContent: "flex-start" }}
        >
          <Typography>{`${params?.row?.fullName}`}</Typography>
          <Typography>{params?.row?.contactPhoneNumber
            ? params?.row?.contactPhoneNumber
            : params.row?.emailAddress
              ? params.row?.emailAddress
              : ""}
          </Typography>
        </Stack>
      }
    },
    {
      field: "status",
      headerName: t("platform.status"),
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        const style = getStatusStyle(status);

        if (status === "new") {
          return <Typography sx={style}>{params.value}</Typography>;
        }

        return (
          <Chip
            label={params.value}
            sx={style}
          />
        );
      }
    },
    {
      field: "assignedChannel",
      headerName: t("platform.assignedChannel"),
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => {
        if (!params.value) {
          return "";
        }

        return (
          <Chip
            label={params.value}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              backgroundColor: "white",
              borderColor: "#d0d0d0",
              color: "#333",
              fontWeight: 500,
              fontSize: "0.875rem"
            }}
          />
        )
      }

    },
    // {
    //   field: "suggestedType",
    //   headerName: "Suggested Type",
    //   width: 250,
    //   sortable: false,
    //   renderCell: (params) => {
    //     return (
    //       <Typography
    //         sx={{
    //           whiteSpace: "normal",
    //           wordWrap: "break-word",
    //           lineHeight: 1.2,
    //           fontSize: "0.875rem"
    //         }}
    //       >
    //         {params.value.join(", ")}
    //       </Typography>
    //     )
    //   }
    // },
    // {
    //   field: "lastContacted",
    //   headerName: "Last Contacted",
    //   width: 160,
    //   renderCell: (params) => {
    //     return (
    //       <Chip
    //         label={params.value}
    //         variant="outlined"
    //         sx={{
    //           borderRadius: "8px",
    //           backgroundColor: "white",
    //           borderColor: "#d0d0d0",
    //           color: "#333",
    //           fontWeight: 500,
    //           fontSize: "0.875rem"
    //         }}
    //       />
    //     )
    //   }
    // },
    {
      field: "suggestEtaText",
      headerName: t("platform.suggestETA"),
      width: 180,
      sortable: false,
      filterable: false,
      valueGetter: (params) => {
        return new Date(params.row.suggestEta).getTime();
      },
      sortComparator: (v1, v2) => {
        return v1 - v2;
      },
      renderCell: (params) => {
        if (params.row.status?.toLowerCase() !== "new" || !params.row.assignedChannel || !params.row.suggestEtaText) {
          return "";
        }

        return (
          <Chip
            label={params.row.suggestEtaText}
            color="primary"
            sx={{ borderRadius: "8px" }}
          />
        )
      }
    },
    // {
    //   field: "assignedTo",
    //   headerName: "Assigned To",
    //   width: 160,
    //   renderCell: (params) => {
    //     return <Typography>{params.value || "Not yet assigned"}</Typography>
    //   }
    // },
    {
      field: "lastContactDate",
      headerName: t("platform.lastContact"),
      sortable: false,
      filterable: false,
      width: 160,
      renderCell: (params) => {
        return (
          <Typography>{params.value ? formatDateToMMDDYYYY(params.value) : "-"}</Typography>
        )
      }
    },
    // {
    //   field: "availableInOmne",
    //   headerName: "Available in Omne",
    //   width: 160,
    //   renderCell: (params) => {
    //     return params.value ? <Chip label={"Yes"} color="primary" sx={{ borderRadius: "8px" }} /> : <Chip label={"No"} sx={{ borderRadius: "8px" }} />
    //   }
    // },
  ];


  const onStatusChange = (val: string) => {
    setValue("status", val);
    performSearch({ status: val });
  }

  const onCriteriaOptionChange = (val: string) => {
    if (val === "ALL") {
      setValue("searchCriteriaOption", "");
      setValue("searchText", "");
      performSearch({ query: {} });
    } else {
      setValue("searchCriteriaOption", val);
      performSearch({ searchCriteriaOption: val });
    }
  }

  const onSearchTextChange = (val: string) => {
    setValue("searchText", val);
    // Don't auto-search on text change, wait for user to click search or press enter
  }

  const onQualificationChange = (val: string) => {
    setValue("qualification", val);
    performSearch({ qualification: val });
  }

  const onSourceChannelChange = (val: string) => {
    setValue("sourceChannel", val);
    performSearch({ targetChannel: val });
  }

  const onAssignedChannelChange = (val: string) => {
    setValue("assignedChannel", val);
    performSearch({ targetChannel: val });
  }

  const gridFiltersToApi = (model: GridFilterModel) => {
    const filterStatuses: string[] = [];
    const filterSourceChannels: string[] = [];
    const filterAssignedChannels: string[] = [];
    const filterQualifications: string[] = [];
    let query: Record<string, string> = {};

    for (const item of model.items ?? []) {
      const field = item.field;
      const val = (item.value ?? "").toString().trim();
      if (!val) continue;
      switch (field) {
        case "status":
          filterStatuses.push(val);
          break;
        case "channel":
          filterSourceChannels.push(val);
          break;
        case "assignedChannel":
          filterAssignedChannels.push(val);
          break;
        case "qualification":
          filterQualifications.push(val);
          break;
        case "profile":
          query = { name: val };
          break;
        default:
          break;
      }
    }

    return { query, filterStatuses, filterSourceChannels, filterAssignedChannels, filterQualifications };
  };

  const performSearch = (overrides: any = {}) => {
    const currentFormValue = formMethods.getValues();
    const searchFieldList = ["status", "searchCriteriaOption", "qualification", "targetChannel"];
    const pageReset = some(searchFieldList, field => has(overrides, field))

    if (pageReset) {
      setCurrentPage(1);
    }

    // Merge current values with any overrides
    const searchParams = {
      status: currentFormValue.status,
      sourceChannel: currentFormValue.sourceChannel,
      assignedChannel: currentFormValue.assignedChannel,
      qualification: currentFormValue.qualification,
      searchCriteriaOption: currentFormValue.searchCriteriaOption,
      searchText: currentFormValue.searchText?.trim() || "",
      currentPage: pageReset ? 1 : currentPage,
      pageSize: 10,
      ...overrides
    };

    const query = (searchParams.searchCriteriaOption && searchParams.searchText)
      ? { [searchParams.searchCriteriaOption]: searchParams.searchText?.trim() }
      : {};

    let apiFiltersFromGrid = { query: {}, filterStatuses: [], filterSourceChannels: [], filterAssignedChannels: [], filterQualifications: [] } as any;
    if (overrides.gridFilterModel) {
      apiFiltersFromGrid = gridFiltersToApi(overrides.gridFilterModel);
    }
    const finalQuery = Object.keys(apiFiltersFromGrid.query ?? {}).length > 0 ? apiFiltersFromGrid.query : query;

    searchLeadsMutation.mutate({
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
      query: finalQuery,
      filterStatuses:
        (apiFiltersFromGrid.filterStatuses?.length ?? 0) > 0
          ? apiFiltersFromGrid.filterStatuses
          : (searchParams.status === "All Statuses" ? [] : [searchParams.status]),
      filterSourceChannels:
        (apiFiltersFromGrid.filterSourceChannels?.length ?? 0) > 0
          ? apiFiltersFromGrid.filterSourceChannels
          : (searchParams.sourceChannel === "All Channels" ? [] : [searchParams.sourceChannel]),
      filterAssignedChannels:
        (apiFiltersFromGrid.filterAssignedChannels?.length ?? 0) > 0
          ? apiFiltersFromGrid.filterAssignedChannels
          : (searchParams.assignedChannel === "All Channels" ? [] : [searchParams.assignedChannel]),
      filterQualifications:
        (apiFiltersFromGrid.filterQualifications?.length ?? 0) > 0
          ? apiFiltersFromGrid.filterQualifications
          : (searchParams.qualification === "All" ? [] : [searchParams.qualification]),
      filteredCountries: [],
      pagination: {
        CurrentPage: pageReset ? 1 : searchParams.currentPage,
        pageSize: searchParams.pageSize
      }
    });
  }

  const onSearchClick = () => {
    setCurrentPage(1)
    performSearch();
  }

  const onPageChange = (newPage: number) => {
    setCurrentPage(newPage);
    performSearch({ currentPage: newPage });
  }

  const onRowClick = (rowData: LeadData) => {
    setIsModalOpen(true);
    setSelectedRowData(rowData);

    // Trigger lead details mutation when row is clicked
    leadDetailsMutation.mutate({
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
      id: rowData.id
    });

    userInterestsDataMutation.mutate({
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
      id: rowData.id
    });
  }

  const onCloseModal = () => {
    setIsModalOpen(false);
    setEditNotes("")
    setEditStatus(null)
  }

  const onUpdateLeadDetailSubmit = (data: UpdateLeadDetailPayload) => {
    updateLeadDetailsMutation.mutate({
      ...data,
      permission_Token: usePermissionStore.getState().permissionToken ?? ""
    });
  }

  const onFilterModelChange = (model: GridFilterModel) => {
    setCurrentPage(1);
    performSearch({ currentPage: 1, gridFilterModel: model });
  };

  const onSortModelChange = (model: GridSortModel) => {
    // TBD
  };

  return {
    filterLeadsData,
    columns,
    formMethods,
    onStatusChange,
    statusOptionsArray,
    onSourceChannelChange,
    sourceChannelOptionsArray,
    onAssignedChannelChange,
    assignedChannelOptionsArray,
    isLeadsLoading,
    pageSize: 10,
    currentPage,
    onPageChange,
    onCriteriaOptionChange,
    onSearchTextChange,
    onSearchClick,
    onRowClick,
    isModalOpen,
    selectedLeadDetail,
    userInterestsData,
    onCloseModal,
    countryOptions,
    editedNotes,
    editedStatus,
    setEditNotes,
    setEditStatus,
    isMaskedDataLevel,
    filterQualificationOptions,
    customerStatusOptions,
    totalNumberOfLeads,
    paginationData,
    criteriaOptions,
    selectedLeadData,
    isLoadingLeadData,
    isDetailUpdating,
    isLoadingUserInterestsData,
    isActivityLogLoading,
    onUpdateLeadDetailSubmit,
    canEdit,
    activityData,
    onQualificationChange,
    originalRemarks: selectedLeadData?.data?.remarks ?? "",
    originalCustomerResponse: selectedLeadData?.data?.customerResponse ?? "",
    onFilterModelChange,
    onSortModelChange,
  }
}

export default useAllLeads;

